import React, { useEffect, useMemo, useState } from "react";
import { api, money } from "../lib/api";
import { toast } from "sonner";
import {
  ShoppingCart, X, Loader2, Plus, Trash2, Camera, Wallet, Sparkles, Check,
} from "lucide-react";
import { SupplierAutocomplete } from "./SupplierAutocomplete";

const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition";
const labelCls = "block text-[10px] font-bold uppercase text-slate-500 mb-1";

/**
 * "+ Direct Purchase" — button + modal exposed on the Purchasing page.
 * Lets purchasing register an ad-hoc buy (Home Depot, Amazon, etc) that:
 *   1. Records the expense (creates a MaterialRequest kind='direct_purchase',
 *      status='purchased', paid=True) so the field crew still receives items.
 *   2. Posts the outflow to the finance ledger + deducts the paying account.
 *   3. Links to a service call OR a project (+ optional phase/scope) so the
 *      cost lands on the right P&L, and the receive flow shows up in the
 *      correct place for the field team.
 */
export function DirectPurchaseButton({ onDone }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        data-testid="direct-purchase-btn"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white text-sm font-semibold px-4 py-2 hover:bg-slate-800 transition-colors"
      >
        <ShoppingCart className="w-4 h-4" /> New Purchase
      </button>
      {open && (
        <DirectPurchaseModal
          onClose={() => setOpen(false)}
          onDone={() => { setOpen(false); onDone && onDone(); }}
        />
      )}
    </>
  );
}

function DirectPurchaseModal({ onClose, onDone }) {
  const [accounts, setAccounts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const today = new Date().toISOString().slice(0, 10);

  const [form, setForm] = useState({
    supplier_name: "",
    supplier_id: "",
    link_type: "project",   // "project" | "ticket"
    project_id: "",
    scope_id: "",
    ticket_id: "",
    purchased_date: today,
    tax: 0,
    notes: "",
    paid_from_account_id: "",
    payment_method: "credit_card",
    photos: [],
  });
  const [items, setItems] = useState([{ description: "", quantity: 1, unit: "unit", unit_price: 0 }]);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/finance/accounts").catch(() => []),
      api.get("/projects").catch(() => []),
      api.get("/tickets").catch(() => []),
    ])
      .then(([accts, prjs, tks]) => {
        if (!alive) return;
        const activeAccts = (accts || []).filter((a) => a.active !== false);
        setAccounts(activeAccts);
        setProjects(prjs || []);
        setTickets((tks || []).filter((t) => !["completed", "cancelled"].includes(t.status)));
        if (activeAccts.length > 0) setForm((f) => ({ ...f, paid_from_account_id: activeAccts[0].id }));
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const selectedProject = useMemo(
    () => projects.find((p) => p.id === form.project_id),
    [projects, form.project_id],
  );
  const projectScopes = selectedProject?.scopes || [];

  const subtotal = useMemo(
    () => items.reduce((a, it) => a + Number(it.quantity || 0) * Number(it.unit_price || 0), 0),
    [items],
  );
  const grandTotal = useMemo(() => subtotal + Number(form.tax || 0), [subtotal, form.tax]);

  const patchItem = (idx, patch) => {
    setItems(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };
  const addItem = () => setItems([...items, { description: "", quantity: 1, unit: "unit", unit_price: 0 }]);
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const onPhotoAdd = (files) => {
    const arr = Array.from(files || []);
    arr.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => setForm((f) => ({ ...f, photos: [...(f.photos || []), reader.result] }));
      reader.readAsDataURL(file);
    });
  };

  const scanReceiptWithAI = async () => {
    if (!form.photos || form.photos.length === 0) {
      toast.error("Add a receipt photo first");
      return;
    }
    setScanning(true);
    try {
      const res = await api.post("/purchasing/scan-receipt-b64", { photos: form.photos });
      const scanned = (res.items || []).map((it) => ({
        description: it.description || "",
        quantity: Number(it.quantity || 1),
        unit: it.unit || "unit",
        unit_price: Number(it.unit_price || 0),
      }));
      if (scanned.length > 0) {
        setItems(scanned);
        setForm((f) => ({
          ...f,
          supplier_name: f.supplier_name || res.supplier || "",
          purchased_date: res.purchased_date || f.purchased_date,
          tax: Number(res.tax || f.tax || 0),
        }));
        toast.success(`AI extracted ${scanned.length} item(s)`);
      } else {
        toast.warning("No items detected on the receipt");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const submit = async () => {
    if (!form.supplier_name.trim()) { toast.error("Supplier is required"); return; }
    if (!form.paid_from_account_id) { toast.error("Select an account"); return; }
    if (form.link_type === "project" && !form.project_id) { toast.error("Select a project"); return; }
    if (form.link_type === "ticket" && !form.ticket_id) { toast.error("Select a service call"); return; }
    const clean = items.filter((it) => it.description && Number(it.quantity) > 0 && Number(it.unit_price) >= 0);
    if (clean.length === 0) { toast.error("Add at least one item"); return; }

    const payload = {
      supplier_name: form.supplier_name.trim(),
      supplier_id: form.supplier_id || null,
      items: clean.map((it) => ({
        description: it.description,
        quantity: Number(it.quantity),
        unit: it.unit || "unit",
        unit_price: Number(it.unit_price),
      })),
      tax: Number(form.tax || 0),
      purchased_date: form.purchased_date,
      notes: form.notes,
      photos: form.photos,
      paid_from_account_id: form.paid_from_account_id,
      payment_method: form.payment_method,
    };
    if (form.link_type === "project") {
      payload.project_id = form.project_id;
      if (form.scope_id) payload.scope_id = form.scope_id;
    } else {
      payload.ticket_id = form.ticket_id;
    }

    setBusy(true);
    try {
      const res = await api.post("/purchasing/direct-purchase", payload);
      toast.success(`Purchase ${res.number} recorded — posted to ledger`);
      onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to create direct purchase");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl"
           onClick={(e) => e.stopPropagation()}
           data-testid="direct-purchase-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-base font-semibold inline-flex items-center gap-2"><ShoppingCart className="w-4 h-4" /> New Direct Purchase</h2>
            <div className="text-[11px] text-slate-500 mt-0.5">Ad-hoc buy · records expense · posts to ledger · field crew receives items with photo.</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Supplier + linkage */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Supplier *</label>
              <input type="text" value={form.supplier_name}
                data-testid="dp-supplier"
                onChange={(e) => setForm({ ...form, supplier_name: e.target.value })}
                placeholder="Home Depot, Amazon, Lowe's..."
                className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Purchased date</label>
              <input type="date" value={form.purchased_date}
                onChange={(e) => setForm({ ...form, purchased_date: e.target.value })}
                className={inputCls} />
            </div>
          </div>

          {/* Link to project OR ticket */}
          <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
            <div className="text-[10px] font-bold uppercase text-slate-500 mb-2">Cost destination *</div>
            <div className="flex gap-2 mb-2">
              <button
                type="button"
                data-testid="dp-link-project"
                onClick={() => setForm({ ...form, link_type: "project", ticket_id: "" })}
                className={`flex-1 text-xs font-bold uppercase py-1.5 rounded ${form.link_type === "project" ? "bg-slate-900 text-white" : "bg-white border border-slate-300 text-slate-600"}`}
              >Project</button>
              <button
                type="button"
                data-testid="dp-link-ticket"
                onClick={() => setForm({ ...form, link_type: "ticket", project_id: "", scope_id: "" })}
                className={`flex-1 text-xs font-bold uppercase py-1.5 rounded ${form.link_type === "ticket" ? "bg-slate-900 text-white" : "bg-white border border-slate-300 text-slate-600"}`}
              >Service Call</button>
            </div>
            {form.link_type === "project" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <select data-testid="dp-project"
                  value={form.project_id}
                  onChange={(e) => setForm({ ...form, project_id: e.target.value, scope_id: "" })}
                  className={inputCls}>
                  <option value="">— select project —</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.number || ""} · {p.name}</option>
                  ))}
                </select>
                <select data-testid="dp-scope"
                  value={form.scope_id}
                  onChange={(e) => setForm({ ...form, scope_id: e.target.value })}
                  disabled={!form.project_id || projectScopes.length === 0}
                  className={`${inputCls} disabled:bg-slate-100`}>
                  <option value="">— scope (optional, enables WBS progress) —</option>
                  {projectScopes.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
            ) : (
              <select data-testid="dp-ticket"
                value={form.ticket_id}
                onChange={(e) => setForm({ ...form, ticket_id: e.target.value })}
                className={inputCls}>
                <option value="">— select service call —</option>
                {tickets.map((t) => (
                  <option key={t.id} value={t.id}>{t.number || t.id.slice(0, 6)} · {t.title}</option>
                ))}
              </select>
            )}
          </div>

          {/* Photos + AI scan */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className={labelCls}>Receipt photos</label>
              {form.photos.length > 0 && (
                <button
                  type="button"
                  data-testid="dp-ai-scan"
                  onClick={scanReceiptWithAI}
                  disabled={scanning}
                  className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-fuchsia-700 bg-fuchsia-50 border border-fuchsia-200 px-2 py-1 rounded hover:bg-fuchsia-100 disabled:opacity-50"
                >
                  {scanning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Extract items with AI
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {form.photos.map((p, i) => (
                <div key={i} className="relative">
                  <img src={p} alt="" className="w-20 h-20 object-cover rounded-lg border border-slate-200" />
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, photos: form.photos.filter((_, j) => j !== i) })}
                    className="absolute -top-2 -right-2 bg-white border border-slate-300 rounded-full p-0.5 hover:bg-rose-50"
                  ><X className="w-3 h-3" /></button>
                </div>
              ))}
              <label className="w-20 h-20 flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-lg text-slate-400 hover:border-slate-500 cursor-pointer">
                <Camera className="w-5 h-5" />
                <span className="text-[10px] mt-0.5">Add</span>
                <input type="file" accept="image/*" capture="environment" multiple
                  data-testid="dp-photo-input"
                  onChange={(e) => onPhotoAdd(e.target.files)}
                  className="hidden" />
              </label>
            </div>
          </div>

          {/* Items */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b">
              <div className="text-[10px] font-bold uppercase text-slate-500">Items</div>
              <button
                type="button"
                data-testid="dp-add-item"
                onClick={addItem}
                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-slate-700 hover:text-slate-900"
              >
                <Plus className="w-3 h-3" /> Add item
              </button>
            </div>
            <div className="grid grid-cols-12 gap-2 px-3 py-1.5 bg-slate-50 border-b text-[10px] font-bold uppercase text-slate-500">
              <div className="col-span-6">Description</div>
              <div className="col-span-2 text-right">Qty</div>
              <div className="col-span-2 text-right">Unit price</div>
              <div className="col-span-1 text-right">Total</div>
              <div className="col-span-1"></div>
            </div>
            {items.map((it, idx) => {
              const total = Number(it.quantity || 0) * Number(it.unit_price || 0);
              return (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center px-3 py-1.5 border-b border-slate-100 last:border-b-0">
                  <input type="text" value={it.description}
                    data-testid={`dp-item-desc-${idx}`}
                    onChange={(e) => patchItem(idx, { description: e.target.value })}
                    placeholder="Item description"
                    className="col-span-6 rounded border border-slate-300 px-2 py-1 text-xs" />
                  <input type="text" inputMode="decimal" value={it.quantity}
                    data-testid={`dp-item-qty-${idx}`}
                    onChange={(e) => patchItem(idx, { quantity: e.target.value.replace(/[^0-9.]/g, "") })}
                    className="col-span-2 rounded border border-slate-300 px-2 py-1 text-xs text-right tabular-nums" />
                  <input type="text" inputMode="decimal" value={it.unit_price}
                    data-testid={`dp-item-price-${idx}`}
                    onChange={(e) => patchItem(idx, { unit_price: e.target.value.replace(/[^0-9.]/g, "") })}
                    className="col-span-2 rounded border border-slate-300 px-2 py-1 text-xs text-right tabular-nums" />
                  <div className="col-span-1 text-right text-xs font-semibold tabular-nums">{money(total)}</div>
                  <div className="col-span-1 flex justify-end">
                    {items.length > 1 && (
                      <button type="button" onClick={() => removeItem(idx)} className="text-slate-400 hover:text-rose-600">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            <div className="grid grid-cols-12 gap-2 items-center px-3 py-2 bg-slate-50 border-t text-xs">
              <div className="col-span-8 text-right text-slate-500">Subtotal</div>
              <div className="col-span-3 text-right font-semibold tabular-nums">{money(subtotal)}</div>
            </div>
            <div className="grid grid-cols-12 gap-2 items-center px-3 py-1 bg-slate-50 text-xs">
              <div className="col-span-6"></div>
              <div className="col-span-2 text-right text-slate-500">Tax</div>
              <input type="text" inputMode="decimal" value={form.tax}
                data-testid="dp-tax"
                onChange={(e) => setForm({ ...form, tax: e.target.value.replace(/[^0-9.]/g, "") })}
                className="col-span-3 rounded border border-slate-300 px-2 py-1 text-xs text-right tabular-nums" />
              <div className="col-span-1"></div>
            </div>
            <div className="grid grid-cols-12 gap-2 items-center px-3 py-2 bg-slate-900 text-white text-sm">
              <div className="col-span-8 text-right font-bold uppercase text-xs">Grand total</div>
              <div className="col-span-3 text-right font-bold tabular-nums text-emerald-300">{money(grandTotal)}</div>
            </div>
          </div>

          {/* Payment */}
          <div className="border border-emerald-200 rounded-lg p-3 bg-emerald-50">
            <div className="text-[10px] font-bold uppercase text-emerald-700 mb-2 inline-flex items-center gap-1.5">
              <Wallet className="w-3.5 h-3.5" /> Payment
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <label className={labelCls}>Paid from account *</label>
                {loading ? (
                  <div className="text-xs text-slate-400"><Loader2 className="w-3 h-3 inline animate-spin" /> Loading...</div>
                ) : accounts.length === 0 ? (
                  <div className="text-xs text-rose-600 py-1 border border-rose-200 rounded bg-rose-50 px-2">
                    No accounts. Go to Finance → Accounts first.
                  </div>
                ) : (
                  <select
                    data-testid="dp-account"
                    value={form.paid_from_account_id}
                    onChange={(e) => setForm({ ...form, paid_from_account_id: e.target.value })}
                    className={inputCls}
                  >
                    {accounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name} · {a.type || "bank"} · {money(a.current_balance || 0)}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <label className={labelCls}>Method</label>
                <select value={form.payment_method}
                  onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
                  className={inputCls}>
                  <option value="credit_card">Credit card</option>
                  <option value="debit_card">Debit card</option>
                  <option value="bank">Bank transfer</option>
                  <option value="check">Check</option>
                  <option value="cash">Cash</option>
                  <option value="wire">Wire</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          </div>

          <div>
            <label className={labelCls}>Notes (optional)</label>
            <textarea rows={2} value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="e.g. Emergency plumbing supplies for Sky Lane job"
              className={inputCls} />
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl sticky bottom-0">
          <button onClick={onClose} disabled={busy}
            className="text-xs px-3 py-1.5 rounded border border-slate-300 hover:bg-white">Cancel</button>
          <button
            data-testid="dp-confirm"
            onClick={submit}
            disabled={busy || accounts.length === 0}
            className="text-xs px-4 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-50 inline-flex items-center gap-1"
          >{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Record purchase &amp; pay</button>
        </div>
      </div>
    </div>
  );
}

export default DirectPurchaseButton;
